import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_JOBS_URL,
  DASHBOARD,
  DASHBOARD_ADMIN_CONFERENCE_CONFLICTS,
  getAdminDeleteJobUrl,
  getAdminRetryJobUrl,
} from '../constants/routes';
import AuthService from '../services/authservices';
import Spinner from '../components/Spinner';

type AdminJob = {
  job_id: string;
  college_name: string;
  sport_code: string;
  season: number;
  status: string;
  created_at: string;
  updated_at: string;
};

const authService = new AuthService();

const DELETABLE_STATUSES = new Set([
  'READY_TO_REVIEW',
  'NEEDS_MANUAL_VERIFICATION',
  'ISSUES_DURING_PROCESSING',
]);

export default function AdminPortal() {
  const [jobs, setJobs] = useState<AdminJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [season, setSeason] = useState<string>('');
  const [workingId, setWorkingId] = useState<string | null>(null);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (season.trim()) params.set('season', season.trim());
    return params.toString();
  }, [season]);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${ADMIN_JOBS_URL}${query ? `?${query}` : ''}`;
      const res = await authService.authenticatedFetch(url);
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to load jobs');
      const data = (await res.json()) as AdminJob[];
      setJobs(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    void loadJobs();
  }, [loadJobs]);

  const handleRetry = async (jobId: string) => {
    setWorkingId(jobId);
    setMessage(null);
    try {
      const res = await authService.authenticatedFetch(getAdminRetryJobUrl(jobId), {
        method: 'POST',
      });
      const data = (await res.json().catch(() => ({}))) as { detail?: string; status?: string };
      if (!res.ok) throw new Error(data.detail || 'Retry failed');
      setMessage(`Job retried. New status: ${data.status || 'unknown'}`);
      await loadJobs();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Retry failed');
    } finally {
      setWorkingId(null);
    }
  };

  const handleDelete = async (jobId: string) => {
    if (!window.confirm(`Delete job ${jobId}? This cannot be undone.`)) return;
    setWorkingId(jobId);
    setMessage(null);
    try {
      const res = await authService.authenticatedFetch(getAdminDeleteJobUrl(jobId), {
        method: 'DELETE',
      });
      const data = (await res.json().catch(() => ({}))) as { detail?: string };
      if (!res.ok) throw new Error(data.detail || 'Delete failed');
      setMessage(`Job ${jobId} deleted.`);
      await loadJobs();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setWorkingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-7xl space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900">Admin Portal</h1>
          <div className="flex items-center gap-4">
            <a
              href={DASHBOARD_ADMIN_CONFERENCE_CONFLICTS}
              className="text-sm font-semibold text-cyan-700 underline"
            >
              Conference Conflicts
            </a>
            <a href={DASHBOARD} className="text-sm text-cyan-700 underline">
              Back to dashboard
            </a>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-slate-700">Season</label>
          <input
            type="number"
            className="w-32 rounded border px-2 py-1 text-sm"
            value={season}
            onChange={(e) => setSeason(e.target.value)}
            placeholder="All"
          />
          <button
            type="button"
            onClick={() => void loadJobs()}
            disabled={loading}
            className="rounded bg-cyan-600 px-3 py-1 text-sm font-semibold text-white hover:bg-cyan-700 disabled:opacity-50"
          >
            Refresh
          </button>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-900">
            {error}
          </div>
        )}
        {message && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-900">
            {message}
          </div>
        )}

        {loading && jobs.length === 0 ? (
          <Spinner />
        ) : (
          <div className="overflow-auto rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left">Job ID</th>
                  <th className="px-3 py-2 text-left">Team</th>
                  <th className="px-3 py-2 text-left">Sport</th>
                  <th className="px-3 py-2 text-left">Season</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Created</th>
                  <th className="px-3 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.job_id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono text-xs">{job.job_id}</td>
                    <td className="px-3 py-2">{job.college_name}</td>
                    <td className="px-3 py-2">{job.sport_code}</td>
                    <td className="px-3 py-2">{job.season}</td>
                    <td className="px-3 py-2">
                      {job.status === 'ISSUES_DURING_PROCESSING' ? (
                        <span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
                          {job.status}
                        </span>
                      ) : (
                        job.status
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {job.created_at ? new Date(job.created_at).toLocaleString() : '-'}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {job.status === 'ISSUES_DURING_PROCESSING' && (
                          <button
                            type="button"
                            title="Reset the job so it can be approved again"
                            disabled={workingId === job.job_id}
                            onClick={() => void handleRetry(job.job_id)}
                            className="rounded bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-200 disabled:opacity-50"
                          >
                            Retrigger
                          </button>
                        )}
                        {DELETABLE_STATUSES.has(job.status) && (
                          <button
                            type="button"
                            disabled={workingId === job.job_id}
                            onClick={() => void handleDelete(job.job_id)}
                            className="rounded bg-red-100 px-2 py-1 text-xs font-semibold text-red-800 hover:bg-red-200 disabled:opacity-50"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {jobs.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-4 text-center text-slate-500">
                      No jobs found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
